import { takeLatest, select, put, all } from 'redux-saga/effects';
import { appNotify, setChannelWorkflowLoadingOverlayState, setChannelWorkflowGridState, setChannelWorkflowGridStateUpdate, setChannelWorkflowGridLayouts, setChannelWorkflowGridSelectedLayoutId,
  fetchChannelWorkflowGridLayouts, saveChannelWorkflowGridLayout, updateChannelWorkflowGridLayout, deleteChannelWorkflowGridLayout,
  favoriteChannelWorkflowGridLayout, setChannelWorkflowGridLayoutIsDiverted, setPostAcqWorkflowGridState,
  setPostAcqWorkflowGridSelectedLayoutId, setPostAcqWorkflowGridLayoutIsDiverted, setPostAcqWorkflowLoadingOverlayState,
  setPostAcqWorkflowGridLayouts } from 'store/actions';
import { appSettingsSelector, channelWorkflowGridFieldSettingsSelector,
   channelWorkflowGridSelectedLayoutIdSelector, channelWorkflowStateSelector, postAcqWorkflowGridSelectedLayoutIdSelector } from 'store/selectors';
import { getWithToken, postWithToken, putWithToken } from 'api/network';
import { sendClientLog } from 'services/logService';

const mergeWithFieldSettings = (appSettings, fieldSettings, stateFromApi, layoutType) => { // Ensure grid has latest fields by merging layout fields into FieldSettings
  if (layoutType === 'PostAcquisition') {
    return {
      ...stateFromApi,      
      selectedRowKeys: [] // Clear any selectedRowKeys (leads to excess grid data requests) 
    }
  }
  return {
    ...stateFromApi,
    columns: fieldSettings.map(({ field, displayName, width, type, format, headerFilter, channelWorkflowGrid }) => {
      const { fixed, showInColumnChooser } = channelWorkflowGrid;
      const index = appSettings.FieldIndex.channelWorkflowGrid[field];

      const foundFromApi = stateFromApi.columns.find(col => col.dataField === field);
      if (foundFromApi) return {
        ...foundFromApi,
        visibleIndex: foundFromApi.visible ? foundFromApi.visibleIndex : index // Use saved index for visible columns to maintain any custom order by user, else use default index
      };
      return {
        dataField: field,
        name: field,
        fixed: fixed || false,
        width: width,
        dataType: type,
        format: format,
        visible: false,
        showInColumnChooser: showInColumnChooser === false ? false : true,
        visibleIndex: index,
        groupIndex: undefined
      }
    }),
    selectedRowKeys: [] // Clear any selectedRowKeys (leads to excess grid data requests) 
  }
}
const isPostAcquisitionLayout = (layoutType) => {
  return  layoutType === 'PostAcquisition';
};
const getUrl = (layoutType) => {
  const subPath = isPostAcquisitionLayout(layoutType)  ? 'postacquisition/' : '';
  return `/api/${subPath}layout/`;
}
const setLoadingMessage = (isLoading, message) => {
  return { isLoading: isLoading, message: message };
};
const setLoadingOverlayState = (layOutType, message, isLoading) => {
  var loadingOverlayObject = setLoadingMessage(isLoading, message);
  return isPostAcquisitionLayout(layOutType) ? setPostAcqWorkflowLoadingOverlayState(loadingOverlayObject) : setChannelWorkflowLoadingOverlayState(loadingOverlayObject)
};
const getConditionalSubMessage = (layOutType, isSendClientLog = false) => {  
  return isPostAcquisitionLayout(layOutType) ? 'Post-Acq ' : isSendClientLog ? 'Channel Workflow ' : '';
};
const getSelectedLayoutIdSelector = (layOutType) => {
  return isPostAcquisitionLayout(layOutType) ? postAcqWorkflowGridSelectedLayoutIdSelector : channelWorkflowGridSelectedLayoutIdSelector;
};
const setWorkflowGridLayoutIsDiverted = (layOutType, status = false) => {
  return isPostAcquisitionLayout(layOutType) ? setPostAcqWorkflowGridLayoutIsDiverted(status) : setChannelWorkflowGridLayoutIsDiverted(status);
};
const setGridSelectedLayoutId = (id, layoutType, shouldFetch) => {
  return isPostAcquisitionLayout(layoutType) ?  setPostAcqWorkflowGridSelectedLayoutId(id, layoutType, shouldFetch) :  setChannelWorkflowGridSelectedLayoutId(id, layoutType, shouldFetch);
};
const setGridLayouts = (layoutType, layouts) => {
  return isPostAcquisitionLayout(layoutType) ? setPostAcqWorkflowGridLayouts(layouts) : setChannelWorkflowGridLayouts(layouts);
};
export function* channelWorkflowGridLayoutSaga() {
  yield takeLatest(fetchChannelWorkflowGridLayouts, function* ({ payload: layoutType, withLoadingOverlay = true }) {
    try {
      if (withLoadingOverlay) { 
        yield put(setLoadingOverlayState(layoutType, `Loading saved ${getConditionalSubMessage(layoutType)}layouts...`, true));            
      }
      const res = yield getWithToken(`${getUrl(layoutType)}forUser`);
      const layouts = yield res.json();
      if (!layouts?.length) {
        yield put(appNotify(`No saved ${getConditionalSubMessage(layoutType)} layouts found`, 'warning'));
      }
      yield put(setGridLayouts(layoutType, layouts));
    } catch (error) {
      yield put(appNotify(error.message, 'error'));
    } finally {
      if (withLoadingOverlay) { 
        yield put (setLoadingOverlayState(layoutType, null, false));            
      }
    }
  });

  yield takeLatest(setChannelWorkflowGridSelectedLayoutId, function* ({ payload }) {
    const { layoutType, shouldFetch = true } = payload;
    if (!shouldFetch) return;
    try {
      const [channelWorkflowGridSelectedLayoutId, appSettings, channelWorkflowGridFieldSettings] = yield all([
        select(channelWorkflowGridSelectedLayoutIdSelector), // Get layoutId through selector rather than through payload- allows calling this action with undefined id, to reset grid state based on last id.
        select(appSettingsSelector),
        select(channelWorkflowGridFieldSettingsSelector)
      ]); 
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Loading layout...' }));
      
      let res = yield getWithToken(`${getUrl(layoutType)}${channelWorkflowGridSelectedLayoutId}`);
      const fetchedLayout = yield res.json();      
      let layoutGridState = mergeWithFieldSettings(appSettings, channelWorkflowGridFieldSettings, JSON.parse(fetchedLayout.jsonValue), layoutType);

      const { channelWorkflowGridStateUpdate } = yield select(channelWorkflowStateSelector);
      if (channelWorkflowGridStateUpdate?.state) {
        layoutGridState = {
          ...layoutGridState,
          ...channelWorkflowGridStateUpdate.state
        };
      }
      
      yield put(setChannelWorkflowGridState(layoutGridState));
      if (channelWorkflowGridStateUpdate?.state) yield put(setChannelWorkflowGridStateUpdate(null));
      yield put(setChannelWorkflowGridLayoutIsDiverted(false));
      
      sendClientLog('Info', `${getConditionalSubMessage(layoutType, true)}- Layouts: User applied layout with id ${channelWorkflowGridSelectedLayoutId}`);
    } catch (error) {
      yield put(appNotify(error.message, 'error'));
      yield put(setChannelWorkflowGridSelectedLayoutId(null, layoutType, false))
    } finally {
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
    }
  });

  yield takeLatest(setPostAcqWorkflowGridSelectedLayoutId, function* ({ payload }) {
    const { layoutType, shouldFetch = true } = payload;
    if (!shouldFetch) return;
    try {
      const [postAcqWorkflowGridSelectedLayoutId, appSettings, channelWorkflowGridFieldSettings] = yield all([
        select(postAcqWorkflowGridSelectedLayoutIdSelector), // Get layoutId through selector rather than through payload- allows calling this action with undefined id, to reset grid state based on last id.
        select(appSettingsSelector),
        select(channelWorkflowGridFieldSettingsSelector)
      ]); 
      yield put(setPostAcqWorkflowLoadingOverlayState({ isLoading: true, message: `Loading ${getConditionalSubMessage(layoutType)}layout...` }));
      
      let res = yield getWithToken(`${getUrl(layoutType)}${postAcqWorkflowGridSelectedLayoutId}`);
      const fetchedLayout = yield res.json();
      let layoutGridState = mergeWithFieldSettings(appSettings, channelWorkflowGridFieldSettings, JSON.parse(fetchedLayout.jsonValue), layoutType);

      yield put(setPostAcqWorkflowGridState(layoutGridState));
      yield put(setPostAcqWorkflowGridLayoutIsDiverted(false));
      
      sendClientLog('Info', `${getConditionalSubMessage(layoutType)}Layouts: User applied layout with id ${postAcqWorkflowGridSelectedLayoutId}`);
    } catch (error) {
      yield put(appNotify(error.message, 'error'));
      yield put(setPostAcqWorkflowGridSelectedLayoutId(null, layoutType, false))
    } finally {
      yield put(setPostAcqWorkflowLoadingOverlayState({ isLoading: false, message: null }));
    }
  });

  yield takeLatest(saveChannelWorkflowGridLayout, function* ({ payload }) {
    const { req, layoutType, callback } = payload;
    try {
      const res = yield postWithToken(`${getUrl(layoutType)}create`, req);
      const createdId = yield res.json();
      yield put(appNotify(`${getConditionalSubMessage(layoutType)}Layout saved`, 'success', { autoHideDuration: 1500 }));
      yield put(fetchChannelWorkflowGridLayouts(layoutType))
      yield put(setGridSelectedLayoutId(createdId, layoutType, false)); // Pass false to skip fetching layout by id and updating grid
      yield put(setWorkflowGridLayoutIsDiverted(layoutType, false));
      sendClientLog('Info', `${getConditionalSubMessage(layoutType)}- Layouts: User created layout with id ${createdId}`);
      callback && callback(true);
    } catch (error) {
      callback && callback(false, error);
      yield put(appNotify(error.message, 'error'));
    }
  });

  yield takeLatest(updateChannelWorkflowGridLayout, function* ({ payload }) {
    const { req, layoutType, callback } = payload;
    const setHideLoadingOverlayState = setLoadingOverlayState(layoutType, null, false);
    try {      
      yield put(setLoadingOverlayState(layoutType, `Updating ${getConditionalSubMessage(layoutType)}layout...`, true));     
      const res = yield putWithToken(`${getUrl(layoutType)}update`, req);
      const updatedId = yield res.json();
      yield put(appNotify('Update Success', 'success', { autoHideDuration: 1500 }));
      yield put(setHideLoadingOverlayState);
      yield put(fetchChannelWorkflowGridLayouts(layoutType));
      yield put(setGridSelectedLayoutId(updatedId, layoutType, false)); // Pass false to skip fetching layout by id and updating grid
      yield put(setWorkflowGridLayoutIsDiverted(layoutType));
      sendClientLog('Info', `${getConditionalSubMessage(layoutType, true)}- Layouts: User updated layout with id ${req.id}`);
      callback && callback();
    } catch (error) {
      yield put(appNotify(error.message, 'error'));
      yield put(setHideLoadingOverlayState);
    }
  });

  yield takeLatest(deleteChannelWorkflowGridLayout, function* ({ payload }) {
    const { id, layoutType, callback } = payload;
    const setHideLoadingOverlayState = setLoadingOverlayState(layoutType, null, false);
    try {     
      const selectedLayoutIdSelector = getSelectedLayoutIdSelector(layoutType);
      const channelWorkflowGridSelectedLayoutId = yield  select(selectedLayoutIdSelector); 
     
      yield put(setLoadingOverlayState(layoutType, `Deleting ${getConditionalSubMessage(layoutType)}layout...`, true));      
      yield putWithToken(`${getUrl(layoutType)}${id}/delete`);
      yield put(appNotify(`${getConditionalSubMessage(layoutType)}Layout deleted`, 'success', { autoHideDuration: 1500 }));
      yield put(setHideLoadingOverlayState);
      yield put(fetchChannelWorkflowGridLayouts(layoutType));
      
      if (id === channelWorkflowGridSelectedLayoutId) {
        yield put(setWorkflowGridLayoutIsDiverted(layoutType));
      }
      sendClientLog('Info', `${getConditionalSubMessage(layoutType, true)}- Layouts: User deleted layout with id ${id}`);
      callback && callback();
    } catch (error) {
       yield put(appNotify(error.message, 'error'));
      yield put(setHideLoadingOverlayState);
    }
  });

  yield takeLatest(favoriteChannelWorkflowGridLayout, function* ({ payload }) {
    const { id, isFavorite, layoutType, successCallback, errorCallback } = payload;
    try {
      yield putWithToken(`${getUrl(layoutType)}${id}/${isFavorite ? 'addFavorite' : 'removeFavorite'}`);
      sendClientLog('Info', `${getConditionalSubMessage(layoutType, true)}- Layouts: User ${isFavorite ? 'added' : 'removed'} favorite for layout with id ${id}`);
      successCallback && successCallback();
    } catch (error) {
      yield put(appNotify(error.message, 'error'));
      errorCallback && errorCallback();
    }
  });

  yield takeLatest(setChannelWorkflowGridStateUpdate, function* ({ payload }) {
    const { state, isUpdate } = payload;
    if (!state) return;
    const { channelWorkflowGridState } = yield select(channelWorkflowStateSelector);
    if (channelWorkflowGridState) {
      yield put(setChannelWorkflowGridState({...channelWorkflowGridState.state, ...state }));
      yield put(setChannelWorkflowGridStateUpdate(null));
    }
  });
}
